/* =====================================================================
   JS MILLENIUM — « Vous aimeriez aussi » + popup « Commander par coloris »
   Utilisé par le tiroir panier (maquette B3 v3) ET la page panier
   (section jsm-panier.liquid).
   ---------------------------------------------------------------------
   1. « Vous aimeriez aussi » : suggestions calculées à partir du panier
      et de l'historique de la cliente. Score de chaque produit :
        1. Portés ensemble   → produits complémentaires (Search & Discovery)
        2. Compléter l'assortiment → catégories absentes du panier,
           d'abord dans les familles déjà achetées (ex. shorts → jupes)
        3. Ses coloris : couleurs de son panier (poids fort) + couleurs de
           ses commandes des 12 derniers mois (poids plus faible).
           Coloris n°1 > n°2 > n°3.
        4. Même saison / capsule (tags pe26, ah26, capsule…)
        5. Ce qu'elle n'a jamais commandé passe devant ce qu'elle a déjà
           (sauf un ancien modèle de retour en stock : on le lui rappelle)
        6. Nouveautés / réassorts / best-sellers
      Une seule pièce par catégorie d'abord (variété), jamais un modèle déjà
      dans le panier. Pas de prix affiché : on donne envie de cliquer.
   2. Popup « Commander par coloris » au clic sur « + » : le contenu est
      rendu par Shopify (templates/product.jsm-qv.liquid). Les coloris
      préférés de la cliente remontent en haut du tableau.
   3. Bandeau « Avantage débloqué » quand la cliente franchit un palier.
   4. Mobile (< 990 px) : carrousel horizontal sous les lignes du panier +
      bande fixe au-dessus du total (3 mini-photos). La bande s'efface quand
      le carrousel est visible ; au toucher, elle ouvre le carrousel dans une
      feuille qui monte du bas.
   ---------------------------------------------------------------------
   Le fichier peut être chargé deux fois (tiroir + page panier) sans
   problème : il ne s'exécute qu'une fois et s'expose dans window.JSM.
   ===================================================================== */

(() => {
  if (window.JSM && window.JSM.panier) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const TIER_KEY = 'jsm-cart-tier';
  const log = (...args) => console.info('[JSM panier]', ...args);

  /* ---------- Utilitaires ---------- */
  const lc = (s) => String(s ?? '').toLowerCase().trim();

  /** « Cabas Eponge Porté Avec Le Haut14387… » → « cabas eponge » */
  const shortName = (title) => {
    const base = String(title || '')
      .split(/\s+(?:porté|portée|avec|vendu|-|–|\|)\s+/i)[0]
      .replace(/\d+/g, '')
      .trim()
      .split(/\s+/)
      .slice(0, 3)
      .join(' ');
    return (base || String(title || '')).toLowerCase();
  };

  /** Format monétaire identique au site (shop.money_format) */
  function formatMoney(cents, format) {
    const fmt = format || '{{amount_with_comma_separator}} €';
    const num = (n, dec, thousands, decimal) => {
      const [int, frac] = (n / 100).toFixed(dec).split('.');
      const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${withSep}${decimal}${frac}` : withSep;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      switch (key) {
        case 'amount':
          return num(cents, 2, ',', '.');
        case 'amount_no_decimals':
          return num(cents, 0, ',', '.');
        case 'amount_no_decimals_with_comma_separator':
          return num(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return num(cents, 2, "'", '.');
        case 'amount_with_space_separator':
          return num(cents, 2, ' ', ',');
        default:
          return num(cents, 2, '.', ',');
      }
    });
  }

  const isColorOption = (name) => {
    const n = lc(name);
    return n.includes('couleur') || n.includes('color') || n.includes('coloris');
  };

  const PLUS_SVG =
    '<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M1 6h10M6 1v10" stroke="currentColor" stroke-width="1.2" fill="none"/></svg>';
  const CLOSE_SVG =
    '<svg width="14" height="14" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="M2 2l14 14M16 2L2 16"/></svg>';

  const tagsOf = (p) =>
    (Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(','))
      .map(lc)
      .filter(Boolean);

  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  function el(tag, attrs = {}, children = []) {
    const node = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => {
      if (v === null || v === undefined || v === false) return;
      if (k === 'text') node.textContent = v;
      else node.setAttribute(k, v === true ? '' : v);
    });
    children.forEach((c) => c && node.appendChild(c));
    return node;
  }

  function sizedImage(url, width) {
    if (!url) return '';
    try {
      const u = new URL(url.startsWith('//') ? `https:${url}` : url, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  /** Normalise un produit (/products/x.js, /recommendations/products.json, /collections/x/products.json) */
  function normalize(raw) {
    const variants = raw.variants || [];
    let img = raw.featured_image || (raw.images && raw.images[0]) || '';
    if (img && typeof img === 'object') img = img.src || '';
    const options = (raw.options || []).map((o, i) => {
      if (typeof o === 'string') {
        const key = `option${i + 1}`;
        return { name: o, values: [...new Set(variants.map((v) => v[key]).filter(Boolean))] };
      }
      return { name: o.name, values: o.values || [] };
    });
    return {
      id: raw.id,
      handle: raw.handle,
      title: raw.title,
      type: lc(raw.type || raw.product_type),
      url: raw.url || `${ROOT}products/${raw.handle}`,
      img,
      tags: tagsOf(raw),
      colors: (options.find((o) => isColorOption(o.name)) || { values: [] }).values,
      available: raw.available !== undefined ? raw.available : variants.some((v) => v.available),
    };
  }

  /* ---------- Phrases (alternées pour ne pas répéter la même) ---------- */
  const PHRASES = {
    pair: ['Se porte avec votre {x}', 'Parfait avec votre {x}', 'À associer à votre {x}'],
    gap: [
      'Pour compléter votre offre',
      'Pour élargir votre sélection',
      'La pièce qui manque à votre vitrine',
      'Idéal pour diversifier votre offre',
    ],
    season: ['De la même collection', "Dans l'esprit de votre commande"],
    colorTop: ['Existe en {x}, votre coloris phare', 'Disponible en {x}, votre coloris n°1'],
    color: ['Existe en {x}', 'Disponible en {x}', 'Dans vos coloris'],
    reorder: ["Vous l'aviez commandé : de retour en stock", 'Votre ancien modèle est réassorti'],
    bestseller: ['Très demandé en ce moment', 'Un best-seller de nos revendeurs'],
    last: ['Plus que quelques pièces', 'Bientôt épuisé'],
    restock: ['De retour en stock', 'Réassorti cette semaine'],
    new: ["Vient d'arriver", 'Tout juste arrivé'],
  };

  /* Badge : un seul par produit, comme sur les cartes du site */
  const BADGES = [
    ['new', 'Nouveauté'],
    ['reassort', 'Réassort'],
    ['bestseller', 'Best-seller'],
    ['last', 'Dernières pièces'],
  ];
  const badgeOf = (tags) => {
    const hit = BADGES.find(([t]) => tags.includes(t) || tags.includes(`badge-${t}`));
    return hit ? hit[1] : '';
  };

  /* ---------- Suggestions ----------
     Pour un affichage immédiat à l'ouverture du tiroir :
       · les suggestions sont calculées en avance, dès que la page est prête
         et à chaque modification du panier, même tiroir fermé ;
       · le résultat est gardé en mémoire (Horizon recrée le panneau à chaque
         mise à jour : le nouveau panneau reprend le dernier résultat) ;
       · à l'ouverture, on affiche tout de suite ce résultat, et on ne refait
         le calcul que si le panier a changé. */
  const SHARED = { fetch: new Map(), memo: new Map() };

  class JsmCartSuggestions {
    constructor(aside) {
      this.aside = aside;
      this.list = aside.querySelector('[data-jsm-sugg-list]');
      this.prefsEl = aside.querySelector('[data-jsm-sugg-prefs]');
      this.emptyEl = aside.querySelector('[data-jsm-sugg-empty]');
      this.tabs = [...aside.querySelectorAll('[data-tab]')];
      this.tab = 'pour-vous';
      this.cache = SHARED.fetch;
      this.ranked = [];
      this.prefs = [];
      this.sig = null;
      this.running = null;
      this.again = false;

      const cfgEl = aside.querySelector('[data-jsm-sugg-config], #jsm-sugg-config');
      try {
        this.cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}');
      } catch (e) {
        this.cfg = {};
      }
      this.memoKey = cfgEl ? cfgEl.textContent.trim() : 'defaut';
      this.count = Number(this.cfg.count) || 5;
      this.seasons = String(this.cfg.seasons || '')
        .split(',')
        .map(lc)
        .filter(Boolean);
      this.parseFamilies(this.cfg.families || '');

      /* Historique de la cliente (facultatif, fourni par le Liquid) :
         pastColors = [["Noir", 12], ["Camel", 4]…] ; ordered = [id produit…] */
      this.pastColors = {};
      (Array.isArray(this.cfg.pastColors) ? this.cfg.pastColors : []).forEach(([name, qty]) => {
        const k = lc(name);
        if (k) this.pastColors[k] = (this.pastColors[k] || 0) + (Number(qty) || 1);
      });
      this.ordered = new Set((Array.isArray(this.cfg.ordered) ? this.cfg.ordered : []).map(Number));
      /* Crédit revendeur : jamais suggéré, et ignoré pour comprendre le panier */
      this.creditHandle = lc(this.cfg.creditHandle || 'credit-revendeurs');

      /* Dernier résultat connu : affichable tout de suite */
      const memo = SHARED.memo.get(this.memoKey);
      if (memo) {
        this.ranked = memo.ranked;
        this.prefs = memo.prefs;
        this.sig = memo.sig;
        quickView.prefs = memo.prefs;
      }

      this.tabs.forEach((btn) =>
        btn.addEventListener('click', () => {
          this.tab = btn.dataset.tab;
          this.tabs.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
          this.render();
        })
      );
    }

    /** Zone où chercher le carrousel mobile : le tiroir, la page panier, sinon la page */
    get scope() {
      return this.aside.closest('dialog') || this.aside.closest('[data-jsm-sugg-scope]') || document;
    }

    /** « hauts: haut, chemise | bas: short, pantalon » → catégorie → famille */
    parseFamilies(text) {
      this.familyOf = new Map();
      String(text)
        .split(/\||\n/)
        .forEach((chunk) => {
          const [fam, cats] = chunk.split(':');
          if (!fam || !cats) return;
          cats
            .split(',')
            .map(lc)
            .filter(Boolean)
            .forEach((cat) => this.familyOf.set(cat, lc(fam)));
        });
    }

    categoryOf(p) {
      const fromTag = p.tags.find((t) => this.familyOf.has(t));
      if (fromTag) return fromTag;
      return this.familyOf.has(p.type) ? p.type : null;
    }

    fetchJSON(url, { cache = true } = {}) {
      if (cache && this.cache.has(url)) return this.cache.get(url);
      const pr = fetch(url, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      if (cache) this.cache.set(url, pr);
      return pr;
    }

    refresh() {
      if (this.running) {
        this.again = true;
        return this.running;
      }
      this.running = this.#compute()
        .catch((err) => {
          this.aside.hidden = true;
          log('panneau masqué : erreur', err);
        })
        .finally(() => {
          this.running = null;
          if (this.again) {
            this.again = false;
            this.refresh();
          }
        });
      return this.running;
    }

    async #compute() {
      const cart = await this.fetchJSON(`${ROOT}cart.js`, { cache: false });
      if (cart && cart.items) cart.items = cart.items.filter((it) => lc(it.handle) !== this.creditHandle);

      /* Panier identique au dernier calcul : on réaffiche sans rien recalculer */
      const sig = cart && cart.items ? cart.items.map((i) => `${i.key}:${i.quantity}`).join('|') : '';
      if (sig && sig === this.sig && this.ranked.length) {
        this.render();
        return;
      }
      this.sig = sig;

      if (!cart || !cart.items || !cart.items.length) {
        this.aside.hidden = true;
        this.ranked = [];
        SHARED.memo.delete(this.memoKey);
        this.#renderMobile();
        log('panneau masqué : panier vide (ou seulement du crédit revendeur)');
        return;
      }

      /* Contexte du panier */
      const cartIds = new Set(cart.items.map((i) => i.product_id));

      /* Coloris préférés : le panier compte double, l'historique simple */
      const colorScore = {};
      cart.items.forEach((it) =>
        (it.options_with_values || []).forEach((o) => {
          if (isColorOption(o.name)) colorScore[lc(o.value)] = (colorScore[lc(o.value)] || 0) + it.quantity * 2;
        })
      );
      Object.entries(this.pastColors).forEach(([k, q]) => {
        colorScore[k] = (colorScore[k] || 0) + q;
      });
      this.prefs = Object.keys(colorScore)
        .sort((a, b) => colorScore[b] - colorScore[a])
        .slice(0, 3);
      quickView.prefs = this.prefs;

      const handles = [...new Map(cart.items.map((i) => [i.product_id, i.handle])).values()].slice(0, 8);
      const cartProducts = (await Promise.all(handles.map((h) => this.fetchJSON(`${ROOT}products/${h}.js`))))
        .filter(Boolean)
        .map(normalize);

      const cartCats = new Set();
      const cartFams = new Set();
      const cartSeasons = new Set();
      cartProducts.forEach((p) => {
        const cat = this.categoryOf(p);
        if (cat) {
          cartCats.add(cat);
          cartFams.add(this.familyOf.get(cat));
        }
        p.tags.forEach((t) => this.seasons.includes(t) && cartSeasons.add(t));
      });

      /* Candidats */
      const cands = new Map();
      const add = (raw, source, pairedWith) => {
        if (!raw || !raw.id) return;
        const p = normalize(raw);
        if (cartIds.has(p.id) || !p.available || lc(p.handle) === this.creditHandle) return;
        const c = cands.get(p.id) || { ...p, pairs: [], sources: new Set() };
        c.sources.add(source);
        if (pairedWith && !c.pairs.includes(pairedWith)) c.pairs.push(pairedWith);
        cands.set(p.id, c);
      };

      const recUrl = (id, intent) => `${ROOT}recommendations/products.json?product_id=${id}&intent=${intent}&limit=8`;
      const top = cartProducts.slice(0, 6);
      const [comp, rel, news, restock] = await Promise.all([
        Promise.all(top.map((p) => this.fetchJSON(recUrl(p.id, 'complementary')).then((r) => ({ p, r })))),
        Promise.all(top.slice(0, 3).map((p) => this.fetchJSON(recUrl(p.id, 'related')))),
        this.cfg.newHandle ? this.fetchJSON(`${ROOT}collections/${this.cfg.newHandle}/products.json?limit=30`) : null,
        this.cfg.restockHandle ? this.fetchJSON(`${ROOT}collections/${this.cfg.restockHandle}/products.json?limit=30`) : null,
      ]);

      comp.forEach(({ p, r }) => (r && r.products ? r.products : []).forEach((x) => add(x, 'comp', p.title)));
      rel.forEach((r) => (r && r.products ? r.products : []).forEach((x) => add(x, 'rel')));
      (news && news.products ? news.products : []).forEach((x) => add(x, 'new'));
      (restock && restock.products ? restock.products : []).forEach((x) => add(x, 'restock'));

      if (cands.size < this.count * 2) {
        const all = await this.fetchJSON(`${ROOT}collections/all/products.json?limit=40`);
        (all && all.products ? all.products : []).forEach((x) => add(x, 'all'));
      }
      log(`suggestions : ${cands.size} candidat(s) hors panier, coloris préférés : ${this.prefs.join(', ') || 'aucun'}`);

      /* Score + type de raison (la phrase est choisie au rendu, en alternance) */
      const COLOR_POINTS = [12, 7, 4];
      this.ranked = [...cands.values()]
        .map((c) => {
          let score = 0;
          const has = (t) => c.tags.includes(t) || c.tags.includes(`badge-${t}`);
          const isNew = has('new') || c.sources.has('new');
          const isRestock = has('reassort') || c.sources.has('restock');
          const isBest = has('bestseller');
          const isLast = has('last');
          const cat = this.categoryOf(c);
          const gap = cat && !cartCats.has(cat);
          const season = c.tags.some((t) => cartSeasons.has(t));
          const ordered = this.ordered.has(Number(c.id));

          /* Coloris : on garde le meilleur rang trouvé */
          const lcColors = c.colors.map(lc);
          let colorRank = -1;
          this.prefs.forEach((pref, i) => {
            if (colorRank === -1 && lcColors.includes(pref)) colorRank = i;
          });
          const colorHits = this.prefs.filter((pref) => lcColors.includes(pref)).length;

          if (c.pairs.length) score += 100;
          if (gap) score += cartFams.has(this.familyOf.get(cat)) ? 50 : 30;
          if (season) score += 20;
          if (colorRank >= 0) score += COLOR_POINTS[colorRank] + (colorHits - 1) * 3;
          if (ordered && isRestock) score += 25;
          else if (ordered) score -= 40;
          else score += 6;
          if (isBest) score += 8;
          if (isNew || isRestock || isLast) score += 3;

          /* Raison affichée : la plus parlante d'abord */
          let why = null;
          if (c.pairs.length) why = { key: 'pair', x: shortName(c.pairs[0]) };
          else if (ordered && isRestock) why = { key: 'reorder' };
          else if (isBest) why = { key: 'bestseller' };
          else if (isLast) why = { key: 'last' };
          else if (gap) why = { key: 'gap' };
          else if (colorRank === 0) why = { key: 'colorTop', x: this.prefs[0] };
          else if (season) why = { key: 'season' };
          else if (colorRank > 0) why = { key: 'color', x: this.prefs[colorRank] };
          else if (isRestock) why = { key: 'restock' };
          else if (isNew) why = { key: 'new' };

          return {
            ...c,
            cat,
            score,
            why,
            isNew,
            isRestock,
            badge: badgeOf(c.tags) || (isNew ? 'Nouveauté' : isRestock ? 'Réassort' : ''),
          };
        })
        .sort((a, b) => b.score - a.score);

      SHARED.memo.set(this.memoKey, { ranked: this.ranked, prefs: this.prefs, sig });
      this.render();
    }

    /** Variété : une pièce par catégorie d'abord, puis on complète */
    #diverse(list, n) {
      const out = [];
      const seen = new Set();
      for (const c of list) {
        const key = c.cat || `id-${c.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(c);
        if (out.length >= n) return out;
      }
      for (const c of list) {
        if (out.length >= n) break;
        if (!out.includes(c)) out.push(c);
      }
      return out;
    }

    /** Phrases alternées : la 1re « Pour compléter… », la suivante « Pour élargir… », etc. */
    #phraser() {
      const used = {};
      return (why) => {
        if (!why) return '';
        const pool = PHRASES[why.key] || [];
        const i = used[why.key] || 0;
        used[why.key] = i + 1;
        return (pool[i % pool.length] || '').replace('{x}', why.x || '');
      };
    }

    /** Affiche le dernier résultat connu, sans attendre le réseau */
    showCached() {
      if (!this.ranked.length) return false;
      if (!this.list.childElementCount || this.aside.hidden) this.render();
      return true;
    }

    get prefsText() {
      if (!this.prefs.length) return '';
      return `D'après vos coloris : ${this.prefs.map((l) => l.charAt(0).toUpperCase() + l.slice(1)).join(', ')}`;
    }

    get title() {
      const t = this.aside.querySelector('.jsm-sugg__title');
      return t ? t.textContent.trim() : 'Vous aimeriez aussi';
    }

    render() {
      this.#renderMobile();
      if (!this.ranked.length) {
        this.aside.hidden = true;
        log('panneau masqué : aucun produit à suggérer');
        return;
      }

      let items = this.ranked;
      if (this.tab === 'nouveautes') items = items.filter((c) => c.isNew);
      if (this.tab === 'reassort') items = items.filter((c) => c.isRestock);
      items = this.#diverse(items, this.count);

      const phrase = this.#phraser();
      if (this.prefsEl) {
        this.prefsEl.textContent = this.prefsText;
        this.prefsEl.hidden = !this.prefsText;
      }

      this.list.replaceChildren(...items.map((c) => this.#item(c, phrase(c.why))));
      if (this.emptyEl) this.emptyEl.hidden = items.length > 0;
      this.aside.hidden = false;
    }

    /* ---------- Mobile : carrousel + bande + feuille ---------- */
    #mobileItems() {
      const phrase = this.#phraser();
      return this.#diverse(this.ranked, this.count).map((c) => ({ c, reason: phrase(c.why) }));
    }

    #card(c, reason) {
      const add = el('button', {
        type: 'button',
        class: 'jsm-sugg-card__add',
        'aria-label': `Commander ${c.title} : choisir coloris et quantités`,
        'aria-haspopup': 'dialog',
      });
      add.innerHTML = PLUS_SVG;
      add.addEventListener('click', () => {
        if (this.sheet && this.sheet.open) this.sheet.close();
        quickView.open(c.handle, add);
      });
      return el('div', { class: 'jsm-sugg-card' }, [
        el('div', { class: 'jsm-sugg-card__media' }, [
          el('a', { href: c.url, tabindex: '-1', 'aria-hidden': 'true' }, [
            c.img ? el('img', { src: sizedImage(c.img, 360), alt: '', loading: 'lazy' }) : null,
          ]),
          add,
        ]),
        c.badge ? el('span', { class: 'jsm-sugg__badge', text: c.badge }) : null,
        el('a', { class: 'jsm-sugg-card__name', href: c.url, text: c.title }),
        reason ? el('span', { class: 'jsm-sugg-card__reason', text: reason }) : null,
      ]);
    }

    /** Redessine le carrousel et la bande sans refaire les calculs (après un re-rendu d'Horizon) */
    repaintMobile() {
      const scope = this.scope;
      const box = scope.querySelector('[data-jsm-sugg-mobile]');
      const strip = scope.querySelector('[data-jsm-sugg-strip]');
      const lost = (box && !box.childElementCount) || (strip && !strip.childElementCount);
      if (this.ranked && this.ranked.length && lost) this.#renderMobile();
    }

    #renderMobile() {
      const scope = this.scope;
      const box = scope.querySelector('[data-jsm-sugg-mobile]');
      const strip = scope.querySelector('[data-jsm-sugg-strip]');
      const items = this.ranked.length ? this.#mobileItems() : [];
      this.mobileItems = items;

      if (box) {
        if (!items.length) {
          box.hidden = true;
        } else {
          box.replaceChildren(
            el('div', { class: 'jsm-sugg-m__head' }, [
              el('h3', { class: 'jsm-sugg-m__title', text: this.title }),
              el('span', { class: 'jsm-sugg-m__hint', text: 'Faites glisser →' }),
            ]),
            this.prefsText ? el('p', { class: 'jsm-sugg-m__prefs', text: this.prefsText }) : null,
            el('div', { class: 'jsm-sugg-m__track' }, items.map(({ c, reason }) => this.#card(c, reason)))
          );
          box.hidden = false;
        }
      }

      if (strip) {
        if (!items.length) {
          strip.hidden = true;
        } else {
          const thumbs = el(
            'span',
            { class: 'jsm-sugg-strip__thumbs', 'aria-hidden': 'true' },
            items.slice(0, 3).map(({ c }) => (c.img ? el('img', { src: sizedImage(c.img, 90), alt: '' }) : null))
          );
          const chevron = el('span', { class: 'jsm-sugg-strip__chevron', 'aria-hidden': 'true' });
          chevron.innerHTML =
            '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.3"><path d="M3 9l4-4 4 4"/></svg>';
          strip.replaceChildren(el('span', { class: 'jsm-sugg-strip__title', text: this.title }), thumbs, chevron);
          strip.setAttribute('aria-label', `${this.title} : voir ${items.length} suggestions`);
          if (!strip.dataset.bound) {
            strip.dataset.bound = '1';
            strip.addEventListener('click', () => this.openSheet());
          }
          strip.hidden = false;
        }
      }

      /* La bande s'efface quand le carrousel est visible dans le panier */
      if (this.io) this.io.disconnect();
      if (strip) strip.classList.remove('is-off');
      /* Vrai conteneur qui défile (selon Horizon : la zone des lignes ou tout le contenu) */
      const stop = scope === document ? document.documentElement : scope;
      let scroller = box ? box.parentElement : null;
      while (scroller && scroller !== stop) {
        const oy = getComputedStyle(scroller).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && scroller.scrollHeight > scroller.clientHeight) break;
        scroller = scroller.parentElement;
      }
      if (scroller === stop) scroller = null;
      if (items.length && strip) log(`mobile : ${items.length} suggestion(s), carrousel ${box ? 'prêt' : 'introuvable'}`);
      if (box && strip && items.length && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          ([entry]) => strip.classList.toggle('is-off', entry.isIntersecting),
          { root: scroller || null, threshold: 0.2 }
        );
        this.io.observe(box);
      }
    }

    openSheet() {
      if (!this.sheet || !document.body.contains(this.sheet)) {
        const d = document.createElement('dialog');
        d.className = 'jsm-sugg-sheet';
        d.setAttribute('aria-labelledby', 'jsm-sugg-sheet-title');
        d.addEventListener('click', (e) => {
          if (e.target === d) d.close();
        });
        d.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') e.stopPropagation();
        });
        document.body.appendChild(d);
        this.sheet = d;
      }
      const close = el('button', { type: 'button', class: 'jsm-sugg-sheet__close', 'aria-label': 'Fermer' });
      close.innerHTML = CLOSE_SVG;
      close.addEventListener('click', () => this.sheet.close());
      this.sheet.replaceChildren(
        el('span', { class: 'jsm-sheet-handle', 'aria-hidden': 'true' }),
        el('div', { class: 'jsm-sugg-sheet__head' }, [
          el('h2', { class: 'jsm-sugg-m__title', id: 'jsm-sugg-sheet-title', text: this.title }),
          close,
        ]),
        this.prefsText ? el('p', { class: 'jsm-sugg-m__prefs', text: this.prefsText }) : null,
        el('div', { class: 'jsm-sugg-m__track' }, (this.mobileItems || []).map(({ c, reason }) => this.#card(c, reason)))
      );
      if (!this.sheet.open) this.sheet.showModal();
    }

    #item(c, reason) {
      const add = el('button', {
        type: 'button',
        class: 'jsm-sugg__add',
        'aria-label': `Commander ${c.title} : choisir coloris et quantités`,
        'aria-haspopup': 'dialog',
      });
      add.innerHTML = PLUS_SVG;
      add.addEventListener('click', () => quickView.open(c.handle, add));

      return el('li', { class: 'jsm-sugg__item' }, [
        el('a', { class: 'jsm-sugg__media', href: c.url, tabindex: '-1', 'aria-hidden': 'true' }, [
          c.img ? el('img', { src: sizedImage(c.img, 160), alt: '', loading: 'lazy', width: '64', height: '80' }) : null,
        ]),
        el('div', { class: 'jsm-sugg__info' }, [
          c.badge ? el('span', { class: 'jsm-sugg__badge', text: c.badge }) : null,
          el('a', { class: 'jsm-sugg__name', href: c.url, text: c.title }),
          reason ? el('span', { class: 'jsm-sugg__reason', text: reason }) : null,
        ]),
        add,
      ]);
    }
  }

  /* ---------- Popup « Commander par coloris » ----------
     Le contenu vient de /products/<handle>?view=jsm-qv (rendu Liquid) :
     même pastilles, même « Réf. », même prix (snippet jsm-prix) et même
     stock « N restantes » que la fiche produit. Ici on gère seulement les
     boutons − / +, le total, la phrase de palier et l'ajout au panier. */
  class JsmQuickView {
    dialog = null;
    opener = null;
    prefs = [];

    #ensure() {
      if (this.dialog && document.body.contains(this.dialog)) return this.dialog;
      const d = document.createElement('dialog');
      d.className = 'jsm-qv';
      d.setAttribute('aria-label', 'Commander par coloris');
      d.addEventListener('click', (e) => {
        if (e.target === d) d.close();
      });
      d.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') e.stopPropagation(); // ne ferme que la popup, pas le tiroir
      });
      d.addEventListener('close', () => {
        if (this.opener && document.body.contains(this.opener)) this.opener.focus();
      });
      document.body.appendChild(d);
      this.dialog = d;
      return d;
    }

    async open(handle, opener) {
      this.opener = opener || null;
      const d = this.#ensure();
      d.replaceChildren(el('p', { class: 'jsm-qv__loading', text: 'Chargement…' }));
      if (!d.open) d.showModal();

      const html = await fetch(`${ROOT}products/${handle}?view=jsm-qv`, { headers: { Accept: 'text/html' } })
        .then((r) => (r.ok ? r.text() : null))
        .catch(() => null);

      if (!html || !html.includes('data-jsm-qv')) {
        d.replaceChildren(
          el('div', { class: 'jsm-qv__loading' }, [
            el('p', { text: 'Impossible de charger ce produit pour le moment.' }),
            el('a', { href: `${ROOT}products/${handle}`, class: 'jsm-qv__more', text: 'Voir la fiche produit' }),
          ])
        );
        log('popup : templates/product.jsm-qv.liquid introuvable ou vide');
        return;
      }

      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      const root = tpl.content.querySelector('[data-jsm-qv]');
      d.replaceChildren(root);
      this.#sortByPrefs(root);
      this.#bind(root);
    }

    /** Les coloris préférés de la cliente remontent en haut du tableau */
    #sortByPrefs(root) {
      if (!this.prefs || !this.prefs.length) return;
      root.querySelectorAll('.jsm-qv__table tbody').forEach((tbody) => {
        const rows = [...tbody.querySelectorAll(':scope > tr')];
        const rank = (tr) => {
          const th = tr.querySelector('th');
          const name = lc(th ? th.textContent : '');
          const i = this.prefs.findIndex((p) => name === p || name.includes(p));
          return i === -1 ? 99 : i;
        };
        rows
          .map((tr, i) => ({ tr, i, r: rank(tr) }))
          .sort((a, b) => a.r - b.r || a.i - b.i)
          .forEach(({ tr }) => tbody.appendChild(tr));
      });
    }

    #bind(root) {
      const d = this.dialog;
      let data = {};
      try {
        data = JSON.parse(root.querySelector('[data-jsm-qv-data]').textContent);
      } catch (e) {
        data = {};
      }
      const priceOf = new Map((data.variants || []).map((v) => [String(v.id), v.price_ht]));
      const money = (cents) => formatMoney(cents, data.money_format);

      const img = root.querySelector('[data-jsm-qv-img]');
      const inputs = [...root.querySelectorAll('[data-jsm-qv-qty]')];
      const summary = root.querySelector('[data-jsm-qv-summary]');
      const impact = root.querySelector('[data-jsm-qv-impact]');
      const error = root.querySelector('[data-jsm-qv-error]');
      const cta = root.querySelector('[data-jsm-qv-cta]');
      const ctaIdle = cta ? cta.textContent.trim() : '';

      root.querySelector('[data-jsm-qv-close]')?.addEventListener('click', () => d.close());

      const clamp = (input) => {
        const max = input.max ? Number(input.max) : Infinity;
        const v = Math.min(max, Math.max(0, parseInt(input.value, 10) || 0));
        input.value = String(v);
        return v;
      };

      const selection = () =>
        inputs
          .map((input) => ({ id: Number(input.dataset.variantId), quantity: clamp(input) }))
          .filter((x) => x.quantity > 0);

      const update = () => {
        const sel = selection();
        const pieces = sel.reduce((s, x) => s + x.quantity, 0);
        const totalHT = sel.reduce((s, x) => s + (priceOf.get(String(x.id)) || 0) * x.quantity, 0);
        if (error) error.hidden = true;
        /* Produit vendu en lot : la quantité saisie est un nombre de lots */
        const parLot = Number(data.lot) || 0;
        const libelle = (n) =>
          parLot > 1
            ? `${n} lot${n > 1 ? 's' : ''} · ${n * parLot} pièces`
            : `${n} ${n > 1 ? 'pièces' : 'pièce'}`;
        if (summary) {
          summary.textContent = pieces
            ? `${libelle(pieces)}${totalHT ? ` · ${money(totalHT)} HT` : ''}`
            : 'Sélectionnez vos quantités';
        }
        if (impact) {
          impact.textContent = tierImpact(totalHT, money);
          impact.hidden = !impact.textContent;
        }
        if (cta) {
          cta.disabled = pieces === 0;
          cta.textContent = pieces ? `Ajouter ${libelle(pieces)} au panier` : ctaIdle;
        }
      };

      root.querySelectorAll('[data-jsm-qv-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const input = btn.closest('[data-jsm-qv-stepper]').querySelector('[data-jsm-qv-qty]');
          input.value = String((parseInt(input.value, 10) || 0) + Number(btn.dataset.jsmQvStep));
          clamp(input);
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
      });

      inputs.forEach((input) => {
        input.addEventListener('input', update);
        input.addEventListener('change', update);
        input.addEventListener('focus', () => {
          const src = input.dataset.image;
          if (img && src) img.src = src;
        });
      });

      cta?.addEventListener('click', async () => {
        const sel = selection();
        if (!sel.length) return;
        cta.disabled = true;
        cta.textContent = 'Ajout en cours…';
        const ok = await addToCart(sel, data.product_id, error);
        if (ok) {
          cta.textContent = 'Ajouté au panier';
          setTimeout(() => d.close(), 500);
        } else {
          update();
        }
      });

      update();
    }
  }

  /** Phrase de palier (« Il vous manque… », « Cet ajout débloque… ») :
      lit la barre de la page panier si on y est, sinon celle du tiroir. */
  function tierImpact(addHT, money) {
    const bar =
      document.querySelector('[data-jsm-paliers-page]') ||
      document.querySelector('.cart-drawer__dialog.jsm-cart .jsm-paliers');
    if (!bar) return '';
    let steps = [];
    try {
      steps = JSON.parse(bar.getAttribute('data-steps') || '[]');
    } catch (e) {
      return '';
    }
    const ht = Number(bar.getAttribute('data-ht') || 0);
    const after = ht + addHT;
    const next = steps.find(([m]) => m > ht);
    if (!next) return '';
    if (addHT === 0) return `Il vous manque ${money(next[0] - ht)} HT pour ${next[1]}.`;
    const reached = steps.filter(([m]) => m > ht && m <= after);
    if (reached.length) return `Cet ajout débloque ${reached[reached.length - 1][1]}.`;
    return `Après cet ajout, il vous manque ${money(next[0] - after)} HT pour ${next[1]}.`;
  }

  /** Ajout au panier puis mise à jour du tiroir Horizon (et de la page panier via jsm:cart:updated) */
  async function addToCart(items, productId, errorEl) {
    const cartItems = document.querySelector('.cart-drawer__dialog.jsm-cart cart-items-component');
    const sectionId = cartItems && cartItems.dataset ? cartItems.dataset.sectionId : null;
    const fail = (msg) => {
      if (errorEl) {
        errorEl.textContent = msg;
        errorEl.hidden = false;
      }
      return false;
    };
    try {
      const res = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, ...(sectionId ? { sections: sectionId, sections_url: window.location.pathname } : {}) }),
      });
      const data = await res.json();
      if (!res.ok || data.status) return fail(data.description || data.message || "L'ajout n'a pas pu se faire.");

      const cart = await fetch(`${ROOT}cart.js`).then((r) => r.json()).catch(() => null);
      const detail = {
        resource: data,
        sourceId: 'jsm-quick-view',
        data: { source: 'jsm-quick-view', productId, itemCount: cart ? cart.item_count : undefined, sections: data.sections },
      };
      /* Même forme que CartAddEvent d'Horizon : tiroir et pastille se mettent à jour */
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('jsm:cart:updated', { detail: { cart, source: 'jsm-quick-view' } }));
      return true;
    } catch (e) {
      return fail("L'ajout n'a pas pu se faire. Réessayez.");
    }
  }

  const quickView = new JsmQuickView();

  /* ---------- Bandeau « Avantage débloqué » ---------- */
  function makeTierWatcher(dialog) {
    let hideTimer;
    return () => {
      if (!dialog.open) return; // le bandeau s'affichera à la prochaine ouverture
      const bar = dialog.querySelector('.jsm-paliers');
      if (!bar) return;

      const tier = Number(bar.dataset.tier || 0);
      let prev = null;
      try {
        prev = sessionStorage.getItem(TIER_KEY);
        sessionStorage.setItem(TIER_KEY, String(tier));
      } catch (e) {
        /* navigation privée : pas de bandeau */
      }
      if (prev === null || tier <= Number(prev)) return;

      const toast = bar.querySelector('.jsm-paliers__toast');
      const text = bar.getAttribute(`data-toast-${tier}`);
      if (!toast || !text) return;

      toast.querySelector('.jsm-paliers__toast-text').textContent = text;
      toast.hidden = false;
      toast.classList.remove('is-visible');
      void toast.offsetWidth;
      toast.classList.add('is-visible');
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        toast.hidden = true;
      }, 3800);
    };
  }

  /* ---------- Quantité à 0 = retirer la ligne ----------
     Le × est masqué (demande cliente). Deux façons de retirer une ligne :
       · bouton − quand la ligne est à 1 ;
       · taper 0 dans le champ quantité.
     Horizon désactive le − à 1 pièce, et un bouton désactivé ne reçoit plus
     les clics : on le réactive, et en secours on le rend « transparent » aux
     clics (le clic arrive alors sur le sélecteur, qu'on écoute aussi).
     Suppression : bouton natif d'Horizon s'il existe, sinon /cart/change.js
     puis mise à jour du tiroir avec l'événement natif cart:update. */
  const getDialog = () => document.querySelector('.cart-drawer__dialog.jsm-cart');

  (() => {
    if (document.getElementById('jsm-cart-moins-style')) return;
    const st = document.createElement('style');
    st.id = 'jsm-cart-moins-style';
    st.textContent =
      '.jsm-cart .jsm-cart-line .quantity-selector button:disabled{pointer-events:none}' +
      '.jsm-cart .jsm-cart-line.is-retrait{opacity:.4;pointer-events:none;transition:opacity .2s ease}' +
      '.jsm-cart .jsm-cart-model__ref{display:block;margin-top:2px;font-size:11px;letter-spacing:.03em;color:#6B625A}' +
      /* Panier vide : on ne garde que le message « Votre panier est vide » */
      '.cart-drawer__dialog.jsm-cart.cart-drawer--empty{width:min(580px,95vw)!important;background-color:#F4EFE7}' +
      '.jsm-cart.cart-drawer--empty .jsm-sugg,' +
      '.jsm-cart.cart-drawer--empty .jsm-sugg-m,' +
      '.jsm-cart.cart-drawer--empty .jsm-sugg-strip,' +
      '.jsm-cart.cart-drawer--empty .jsm-paliers,' +
      '.jsm-cart.cart-drawer--empty .jsm-cart__colheads,' +
      '.jsm-cart.cart-drawer--empty .cart-drawer__summary{display:none!important}' +
      '.jsm-cart.cart-drawer--empty .cart-drawer__inner,' +
      '.jsm-cart.cart-drawer--empty .jsm-cart__main,' +
      '.jsm-cart.cart-drawer--empty .cart-drawer__content{background:transparent!important}' +
      /* Popup « Commander par coloris » (ordinateur) : tout tient dans l'écran.
         Seul le tableau des coloris défile s'il y en a beaucoup ; le bouton
         « Ajouter au panier » reste toujours visible. */
      '@media screen and (min-width:750px){' +
      '.jsm-qv{max-height:94dvh}' +
      '.jsm-qv__inner{height:min(760px,94dvh)}' +
      '.jsm-qv__body{display:flex;flex-direction:column;overflow:hidden!important;padding-top:clamp(20px,2.4vw,32px);padding-bottom:clamp(16px,2vw,24px)}' +
      '.jsm-qv__body>*{flex-shrink:0}' +
      '.jsm-qv__table-wrap{flex:0 1 auto!important;min-height:0;overflow-y:auto!important;overscroll-behavior:contain}' +
      '.jsm-qv__table thead th{position:sticky;top:0;z-index:1;background:#F7F4EF}' +
      '.jsm-qv__table tbody th,.jsm-qv__table tbody td{height:36px;padding-top:2px;padding-bottom:2px}' +
      '.jsm-qv__step{height:32px}' +
      '.jsm-qv__title{font-size:clamp(22px,2vw,28px)}' +
      '.jsm-qv .jsm-qv__label{margin-top:12px;padding-top:12px}' +
      '.jsm-qv .jsm-qv__impact{margin-top:10px}' +
      '.jsm-qv__foot{margin-top:auto}' +
      '}';
    document.head.appendChild(st);
  })();

  /** Quantité réelle de la ligne : la plus grande entre la valeur affichée et
      celle rendue par Shopify (évite de lire une valeur intermédiaire) */
  const qtyOf = (input) => {
    const shown = parseInt(input.value, 10);
    const rendered = parseInt(input.getAttribute('value'), 10);
    const a = Number.isNaN(shown) ? 0 : shown;
    const b = Number.isNaN(rendered) ? 0 : rendered;
    return Math.max(a, b);
  };

  const armMinusRemove = debounce(() => {
    const dialog = getDialog();
    if (!dialog) return;
    dialog.querySelectorAll('.jsm-cart-line').forEach((row) => {
      const input = row.querySelector('.quantity-selector input');
      const minus = row.querySelector('.quantity-selector button');
      if (!input || !minus) return;
      if (qtyOf(input) <= 1) {
        if (minus.disabled) minus.disabled = false;
        minus.dataset.jsmRemove = '1';
        minus.setAttribute('aria-label', 'Retirer cet article');
      } else {
        delete minus.dataset.jsmRemove;
      }
    });
  }, 60);

  /** Retire une ligne du tiroir */
  async function removeLine(row) {
    if (!row || row.classList.contains('is-retrait')) return;

    const native = row.querySelector(
      '.cart-items__remove, [data-remove-item], button[name="remove"], [on\\:click*="emove"], a[href*="quantity=0"]'
    );
    if (native) {
      row.classList.add('is-retrait');
      native.click();
      setTimeout(() => row.classList.remove('is-retrait'), 2500);
      return;
    }

    const input = row.querySelector('.quantity-selector input');
    const keyEl = row.matches('[data-key]') ? row : row.querySelector('[data-key], [data-line-key]');
    const key = (keyEl && (keyEl.dataset.key || keyEl.dataset.lineKey)) || (input && input.dataset.key) || '';
    const line =
      row.dataset.line ||
      row.dataset.index ||
      (input && (input.dataset.cartLine || input.dataset.line || input.dataset.index)) ||
      '';
    let body = null;
    if (key) body = { id: key, quantity: 0 };
    else if (line && !Number.isNaN(Number(line))) body = { line: Number(line), quantity: 0 };
    if (!body) {
      log('suppression impossible : ni bouton natif, ni clé de ligne trouvés dans', row);
      return;
    }

    const cartItems = getDialog() && getDialog().querySelector('cart-items-component');
    const sectionId = cartItems && cartItems.dataset ? cartItems.dataset.sectionId : null;
    if (sectionId) {
      body.sections = sectionId;
      body.sections_url = window.location.pathname;
    }

    row.classList.add('is-retrait');
    try {
      const res = await fetch(`${ROOT}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const cart = await res.json();
      if (!res.ok || cart.status) throw new Error(cart.description || 'change.js a échoué');
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'jsm-cart-remove',
            data: { source: 'jsm-cart-remove', itemCount: cart.item_count, sections: cart.sections },
          },
        })
      );
      document.dispatchEvent(new CustomEvent('jsm:cart:updated', { detail: { cart, source: 'jsm-cart-remove' } }));
    } catch (e) {
      row.classList.remove('is-retrait');
      log('suppression : erreur', e);
    }
  }

  /* Clic sur − (ou sur le sélecteur quand le − est encore désactivé) à 1 pièce */
  document.addEventListener(
    'click',
    (e) => {
      if (!e.target.closest) return;
      const selector = e.target.closest('.jsm-cart-line .quantity-selector');
      if (!selector) return;
      const minus = selector.querySelector('button');
      const input = selector.querySelector('input');
      if (!minus || !input) return;

      let surMoins = e.target.closest('button') === minus;
      if (!surMoins && e.target === selector && minus.disabled) {
        const r = minus.getBoundingClientRect();
        surMoins = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      }
      if (!surMoins) return;
      const q = qtyOf(input);
      if (q !== 1) return; // au-dessus de 1 : Horizon retire une seule pièce, on ne fait rien
      log(`retrait de la ligne (quantité ${q})`);

      e.preventDefault();
      e.stopImmediatePropagation();
      removeLine(selector.closest('.jsm-cart-line'));
    },
    true
  );

  /* 0 tapé dans le champ quantité */
  document.addEventListener(
    'change',
    (e) => {
      const input = e.target.closest && e.target.closest('.jsm-cart-line .quantity-selector input');
      if (!input || !e.isTrusted) return; // seulement une saisie de la cliente, jamais un changement fait par Horizon
      if (input.value.trim() !== '0') return;
      log('retrait de la ligne (0 saisi)');
      e.preventDefault();
      e.stopImmediatePropagation();
      removeLine(input.closest('.jsm-cart-line'));
    },
    true
  );
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Enter' || !e.target.matches || !e.target.matches('.jsm-cart-line .quantity-selector input')) return;
      if (e.target.value.trim() !== '0') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      removeLine(e.target.closest('.jsm-cart-line'));
    },
    true
  );

  /* ---------- Initialisation du tiroir ----------
     Le tiroir est retrouvé à chaque fois : l'éditeur de thème et Horizon
     peuvent recréer l'en-tête. Les écouteurs sont posés une seule fois. */
  const instances = new WeakMap();
  let tierWatchers = new WeakMap();

  function getSuggestions(dialog) {
    const aside = dialog && dialog.querySelector('[data-jsm-sugg]');
    if (!aside) return null;
    if (!instances.has(aside)) instances.set(aside, new JsmCartSuggestions(aside));
    return instances.get(aside);
  }

  function getTierWatcher(dialog) {
    if (!tierWatchers.has(dialog)) tierWatchers.set(dialog, makeTierWatcher(dialog));
    return tierWatchers.get(dialog);
  }

  /* Calcul des suggestions, tiroir ouvert OU fermé (pour être prêt à l'ouverture) */
  const refreshSugg = debounce(() => {
    const dialog = getDialog();
    if (!dialog) return;
    const sugg = getSuggestions(dialog);
    if (sugg) sugg.refresh();
    else if (dialog.open) log('panneau absent du tiroir : réglage désactivé, ou client non tagué « revendeur-approuve »');
  }, 80);

  /* À l'ouverture : affichage immédiat du dernier résultat, puis vérification */
  const showNow = () => {
    const dialog = getDialog();
    if (!dialog) return;
    const sugg = getSuggestions(dialog);
    if (sugg) sugg.showCached();
  };

  const checkTier = debounce(() => {
    const dialog = getDialog();
    if (dialog) getTierWatcher(dialog)();
  }, 200);

  function init() {
    /* Horizon re-rend le contenu du panier : on redessine le carrousel et la bande mobiles */
    new MutationObserver(
      debounce(() => {
        const dialog = getDialog();
        if (!dialog || !dialog.open) return;
        const sugg = getSuggestions(dialog);
        if (!sugg) return;
        sugg.showCached(); // panneau recréé par Horizon : on remet le dernier résultat
        sugg.repaintMobile();
      }, 40)
    ).observe(document.body, { childList: true, subtree: true });

    new MutationObserver((mutations) => {
      let opened = false;
      let tierChanged = false;
      mutations.forEach((m) => {
        if (m.attributeName === 'open' && m.target.matches && m.target.matches('.cart-drawer__dialog.jsm-cart')) {
          opened = m.target.open;
        }
        if (m.attributeName === 'data-tier') tierChanged = true;
      });
      armMinusRemove();
      if (opened) {
        showNow();
        refreshSugg();
        setTimeout(checkTier, 300);
      }
      if (tierChanged) checkTier();
    }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'data-tier', 'disabled', 'value'] });

    ['cart:update', 'jsm:cart:updated'].forEach((name) =>
      document.addEventListener(name, () => {
        refreshSugg();
        setTimeout(checkTier, 400);
        setTimeout(armMinusRemove, 400);
      })
    );

    document.addEventListener('shopify:section:load', () => {
      tierWatchers = new WeakMap();
      refreshSugg();
    });

    /* Calcul en avance, quand le navigateur est libre */
    const prefetch = () => {
      refreshSugg();
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(prefetch, { timeout: 2000 });
    else setTimeout(prefetch, 800);
  }

  /* Exposé pour la page panier (section jsm-panier) */
  window.JSM = Object.assign(window.JSM || {}, {
    panier: true,
    CartSuggestions: JsmCartSuggestions,
    quickView,
    formatMoney,
  });
  document.dispatchEvent(new CustomEvent('jsm:panier:pret'));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();